import { createClient } from '@supabase/supabase-js';

// Use environment variables with Vite's import.meta.env instead of process.env
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

console.log('Creating Supabase client with URL:', supabaseUrl);

// Create Supabase client with enhanced configuration for mobile
const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true
  },
  global: {
    headers: {
      'Cache-Control': 'no-store, no-cache',
      'Pragma': 'no-cache'
    },
    fetch: (url, options) => {
      // Use a longer timeout for fetch requests (60 seconds for mobile)
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => timeoutController.abort(), 60000); // Increased to 60 seconds
      
      // Add the abort signal to the fetch options
      options.signal = timeoutController.signal;
      
      // Add keep-alive connection and mobile-friendly headers
      if (!options.headers) options.headers = {};
      options.headers['Connection'] = 'keep-alive';
      options.headers['User-Agent'] = navigator.userAgent;
      
      // Add retry logic for failed requests
      const fetchWithRetry = async (retryCount = 0, maxRetries = 2) => {
        try {
          const response = await fetch(url, options);
          
          // If response is not ok and we haven't exceeded retries, try again
          if (!response.ok && retryCount < maxRetries) {
            // Don't retry on 400 errors (bad request) as they're likely client-side issues
            if (response.status === 400) {
              console.log('Request failed with 400 status, not retrying');
              return response;
            }
            
            console.log(`Request failed with status ${response.status}, retrying... (${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
            return fetchWithRetry(retryCount + 1, maxRetries);
          }
          
          return response;
        } catch (error) {
          if (retryCount < maxRetries && (error.name === 'AbortError' || error.message?.includes('timeout'))) {
            console.log(`Request timeout, retrying... (${retryCount + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
            return fetchWithRetry(retryCount + 1, maxRetries);
          }
          throw error;
        }
      };
      
      return fetchWithRetry()
        .finally(() => clearTimeout(timeoutId));
    }
  },
  realtime: {
    timeout: 60000, // Increased timeout for realtime connections
    params: {
      eventsPerSecond: 1 // Reduced to minimize background data usage
    },
    heartbeatIntervalMs: 20000, // Increased heartbeat interval for better mobile support
    disconnectAfterMs: 120000, // Don't disconnect for 2 minutes of inactivity
  }
});

// Keep connection alive with periodic ping even when tab is inactive
setInterval(() => {
  if (document.visibilityState === 'hidden') {
    console.log('Tab inactive, sending keepalive ping');
    // Simple ping to keep connection alive with retry logic
    const sendKeepalive = async (retryCount = 0) => {
      try {
        // Use a simpler query that's less likely to fail
        const result = await supabase
          .from('game_rooms')
          .select('id')
          .limit(1);
        
        if (result.error) {
          throw result.error;
        }
        console.log('Keepalive ping successful');
      } catch (err) {
        console.error('Keepalive ping failed:', err);
        // Only retry once and only for network errors, not 400 errors
        if (retryCount < 1 && (err.message?.includes('Network') || err.message?.includes('timeout'))) {
          console.log('Retrying keepalive ping...');
          setTimeout(() => sendKeepalive(retryCount + 1), 5000);
        }
      }
    };
    sendKeepalive();
  }
}, 60000); // Increased to 60 seconds to reduce frequency

// Add visibility change handler to refresh connection
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    console.log('Tab became visible, reconnecting if needed');
    // Force reconnect realtime when tab becomes visible again
    try {
      supabase.realtime.setAuth(supabaseAnonKey);
      // Also send a quick ping to ensure connection is alive
      supabase
        .from('game_rooms')
        .select('id')
        .limit(1)
        .then(() => console.log('Connection verified after tab became visible'))
        .catch(err => console.error('Connection check failed after tab became visible:', err));
    } catch (err) {
      console.error('Error reconnecting after tab became visible:', err);
    }
  }
});

// Test the connection with a short timeout request
supabase
  .from('game_rooms')
  .select('id')
  .limit(1)
  .then(result => {
    if (result.error) {
      console.error('Supabase connection test failed:', result.error);
    } else {
      console.log('Supabase connection test succeeded');
    }
  })
  .catch(err => {
    console.error('Supabase connection test failed:', err);
  });

export { supabase };
export default supabase; 